import { useIntl } from "react-intl";
import type { ShellUser } from "../api/types";
import type {
  NavigationConfig,
  SubNavigationConfig,
} from "../components/NavigationBar/type";
import { getCheckRoutes } from "../components/Router/utils";
import {
  type MmRouteConfigMenu,
  type MmRouteConfigSubMenu,
  isMmRouteConfigMenu,
  type IsRouteEnabledOptions,
} from ".";

const getSubNavigationSections = (item: MmRouteConfigMenu) =>
  item.menu.subRouteSections;

const getSubPaths = (item: MmRouteConfigMenu): string[] =>
  item.menu.subRouteSections?.flatMap((section) =>
    section.subRoutes.map((route) => route.path)
  ) || [];

export const useNavigationConfigTransformer = (
  menuDefinition: MmRouteConfigMenu[],
  user: ShellUser | undefined,
  options: IsRouteEnabledOptions
): NavigationConfig => {
  const intl = useIntl();

  const checkRoutes = getCheckRoutes({
    userCountry: user?.account?.country || "",
    userRights: user?.rights || [],
    ...options,
  });

  const filterSubRoutes = (
    subRouteSections?: MmRouteConfigSubMenu[]
  ): MmRouteConfigSubMenu[] | undefined =>
    subRouteSections?.reduce((acc: MmRouteConfigSubMenu[], section) => {
      const filteredSubRoutes = section.subRoutes.reduce(
        (subAcc: MmRouteConfigSubMenu["subRoutes"], subRoute) =>
          checkRoutes(subRoute) ? [...subAcc, subRoute] : subAcc,
        []
      );
      const filteredSection = {
        ...section,
        subRoutes: filteredSubRoutes,
      };
      if (!filteredSection.subRoutes.length) {
        return acc;
      }
      return [...acc, filteredSection];
    }, []);

  const getSubNavigationConfig = (
    subRouteSections?: MmRouteConfigSubMenu[]
  ): SubNavigationConfig[] | undefined => {
    const filteredSub = filterSubRoutes(subRouteSections);
    return filteredSub?.map((section) => ({
      sectionTitle: intl.formatMessage({ id: section.textKey }),
      subRoutes: section.subRoutes.map((subRoute) => ({
        label: subRoute.textKey && intl.formatMessage({ id: subRoute.textKey }),
        href: subRoute.href,
        path: subRoute.path,
        mfe: subRoute.mfe,
      })),
    }));
  };

  const getHref = (item: MmRouteConfigMenu) => {
    const filteredSubRoutes = filterSubRoutes(getSubNavigationSections(item));
    if (
      filteredSubRoutes &&
      !!filteredSubRoutes.length &&
      filteredSubRoutes[0]?.subRoutes[0]?.href
    ) {
      return filteredSubRoutes[0].subRoutes[0].href;
    }
    return item.menu.href;
  };

  const config: NavigationConfig = menuDefinition
    .filter(isMmRouteConfigMenu)
    .filter(checkRoutes)
    .map((item) => ({
      href: getHref(item),
      icon: item.menu.icon,
      key: item.key,
      mfe: item.mfe,
      path: item.path,
      subPaths: getSubPaths(item),
      position: item.menu.position,
      label: intl.formatMessage({ id: item.menu.textKey }),
      subNavigationConfig: getSubNavigationConfig(
        getSubNavigationSections(item)
      ),
    }));
  return config;
};
